import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { FormHelper } from '@helpers/form.helper';
import { enumEntityDataSource } from '@data-layer/enums/isf';
import { DESC_ENTITY } from '@constants/desc-entity';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { CbyIsfStepComponent } from '../cby-isf-step-component/cby-isf-step.component';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { ValidationMessageModel } from '@data-layer/models/common';
import { EntityModel } from '@data-layer/models/entities';
import { IsfPossibleEntitiesService, IsfStateService } from '@shared/isf-mutual/services';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfModel } from '@data-layer/models/isf';

@Component({
  selector: 'entity-unify-default',
  templateUrl: './entity-unify-default.component.html',
  styleUrls: ['./entity-unify-default.component.scss'],
})
export class EntityUnifyDefaultComponent extends CbyIsfStepComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public isfEntityCode: enumEntityModelCode;
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public info = null;

  public company: CompanyModel;

  public formGroup: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm } = this.fh.createErrorsForIsfFormGroup();

  public updateIsfEntity: EntityModel;
  public currentIsfEntity: EntityModel;
  public possibleIsfEntities: EntityModel[] = [];

  private enumEntityDataSource: typeof enumEntityDataSource = enumEntityDataSource;
  private enumIdentityQual: typeof enumIdentifierQualifier = enumIdentifierQualifier;

  private addPart = {};
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private isfStateService: IsfStateService,
    private fh: FormHelper,
    private cd: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private companiesService: CompaniesService,
    private isfPossibleEntitiesService: IsfPossibleEntitiesService,
  ) {
    super();
  }

  public ngAfterViewInit(): void {
    if (!this.fh.isValidIsfEntity(this.currentIsfEntity)) {
      this.fh.setEntityBackError(this.formGroup, this.currentIsfEntity, this.errors);
    }
    this.cd.detectChanges();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.formGroup = this.fh.createIsfEntityFormGroup(this.isfEntityCode, this.isDestroy);
    if (!this.isfStateService.currentIsf) {
      this.router.navigate(['dashboard']);
      return;
    }
    this.company = this.companiesService.getCompany();
    this.info = DESC_ENTITY[this.isfEntityCode];

    this.addPart = {
      entityCode: this.isfEntityCode,
      entityIdentifierQualifier: enumIdentifierQualifier.EIN,
      type:
        [enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.SHIP_TO].indexOf(this.isfEntityCode) !== -1 ? 1 : 2,
    };

    this.checkValidState();
    this.possibleIsfEntities = this.isfPossibleEntitiesService.calculatePossibleIsfEntities(this.isfEntityCode, this.possibleIsfEntities);
    this.updateCurrentIsfEntity();

    this.fh.patchIsfEntity(this.formGroup, this.currentIsfEntity);

    if (!this.formGroup.value.dataSource) {
      const st: EntityModel = this.possibleIsfEntities?.[0];

      if (this.formGroup.value.id) {
        this.formGroup.patchValue({ dataSource: this.currentIsfEntity.id });
      } else {
        this.formGroup.patchValue({ ...st, dataSource: st?.id });
      }
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
    super.ngOnDestroy();
  }

  public continue(): void {
    this.validateForm();

    if (this.formGroup.invalid) {
      const invalidFields: [{ [key: string]: ValidationMessageModel }] = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.openModal(invalidFields);
        return;
      }
    }

    this.save();
  }

  public skip(): void {
    if (!this.currentIsfEntity.id) {
      this.next();
      return;
    }

    this.isfStateService
      .deleteIsfEntity(this.currentIsfEntity.id)
      .pipe(
        catchError(() => EMPTY),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => this.next());
  }

  public confirm(): void {}

  public decline(): void {
    this.save();
  }

  public checkValidState(): void {
    this.isValid = this.formGroup.valid;
    this.subscription.push(
      this.formGroup.statusChanges.subscribe(() => {
        if (this.formGroup.value.dataSource !== this.enumEntityDataSource.NEW) {
          this.isValid = true;
          return;
        }
        const isValidForm: boolean = this.formGroup.get('entityIdentifier').valid && this.formGroup.get('entityIdentifierQualifier').valid;
        if (!isValidForm) {
          this.isValid = isValidForm;
          return;
        }

        this.isValid = !this.fh.isEmptyEntityForm(this.formGroup);
      }),
    );
  }

  public cardLabelIndex(isfEntity: EntityModel): number {
    if (isfEntity.entityCode !== enumEntityModelCode.MANUFACTURER) {
      return;
    }

    return (
      this.possibleIsfEntities.findIndex(
        (item: EntityModel) => item.id === isfEntity.id && item.entityCode === enumEntityModelCode.MANUFACTURER,
      ) || 0
    );
  }

  private save(): void {
    const isfEntityForUpdate: EntityModel = this.prepareToUpdate();

    // TODO: FIX SET ADDRESS_ID
    if (this.fh.isEmptyFormAll(this.formGroup.get('address') as UntypedFormGroup)) {
      isfEntityForUpdate.address = null;
      isfEntityForUpdate.addressId = null;
    }

    if (isfEntityForUpdate.address && !isfEntityForUpdate.address.id) {
      isfEntityForUpdate.address.id = 0;
    }

    if (isfEntityForUpdate.address) {
      isfEntityForUpdate.addressId = isfEntityForUpdate.address.id;
    }

    const keyId: number = enumEntityByKey[this.getEntityKeyByValue(this.isfEntityCode)];

    this.updateCreateEntity(isfEntityForUpdate)
      .pipe(
        switchMap((res: EntityModel) => {
          return this.isfStateService.updateShipment({
            ...this.isfStateService.currentIsf.shipment,
            [keyId]: res.id,
          });
        }),
        switchMap(() => this.isfStateService.getSummaryById(this.isfStateService.currentIsf.referenceNumber)),
        takeUntil(this.isDestroy),
      )
      .subscribe((isf: IsfModel) => {
        this.isfStateService.currentIsf = isf;

        this.next();
      });
  }

  private openModal(data: [{ [key: string]: ValidationMessageModel }]): void {
    this.missingFieldModal.openCustomModal(data);
  }

  private updateCurrentIsfEntity(): void {
    const isfEntity: EntityModel = this.isfStateService.getIsfEntityById(enumEntityByKey[this.getEntityKeyByValue(this.isfEntityCode)]);

    this.currentIsfEntity = !isfEntity
      ? new EntityModel({
          ...this.addPart,
        })
      : new EntityModel(isfEntity);

    this.updateIsfEntity =
      isfEntity && !this.possibleIsfEntities.find((item: EntityModel) => item.id === isfEntity.id)
        ? new EntityModel({ ...this.currentIsfEntity, ...this.addPart })
        : new EntityModel({ ...this.addPart });
  }

  private validateForm(): void {
    const valueFrom: EntityModel = this.formGroup.value;
    const { entityIdentifier, entityIdentifierQualifier } = valueFrom;
    if (!entityIdentifier) {
      this.formGroup.get('entityIdentifierQualifier').reset();
    }
    if (!entityIdentifierQualifier) {
      this.formGroup.get('entityIdentifier').reset();
    }
  }

  private prepareToUpdate(): EntityModel {
    const isfEntity: EntityModel = new EntityModel({
      ...this.formGroup.value,
      entityCode: this.isfEntityCode,
    });

    return isfEntity;
  }

  private updateCreateEntity(entity: EntityModel): Observable<EntityModel> {
    if (entity.id && this.possibleIsfEntities.map((item: EntityModel) => item.id).includes(entity.id)) {
      return of(entity);
    } else {
      return this.isfStateService.updateEntity(entity);
    }
  }

  private getEntityKeyByValue(code: string): string {
    return Object.keys(enumEntityModelCode).find((key: string) => enumEntityModelCode[key] === code);
  }
}
